import React from 'react'
import Checkbox from 'components/checkbox'
import { ITableWithCells, IAccessPolicy, ITag, OptionProps } from 'types'
import { UserRoles, AccessAttributeKinds } from 'app-constants'
import Select from 'components/select'

interface Props {
  tableId: string
  tags: ITableWithCells
  policies: IAccessPolicy[]
  onTagAdd: (tagId: string) => void
  onTagDelete: (tagId: string) => void
  onAllTagsAdd: (tableId: string, role: UserRoles) => void
  onAllTagsDelete: (tableId: string, role: UserRoles) => void
  role: UserRoles
  onBulkTagAdd: (tagIds: string[]) => void
}

const isTagInCurrentTable = (tag: ITag, tableId: string) => {
  /* eslint-disable-next-line */
  const [prefix, tagTableID, _columnId, _rowId] = tag.referencePublicId.split('/')
  return tagTableID === tableId
}

const TagTable: React.FC<Props> = ({
  tableId,
  tags,
  policies,
  onTagAdd,
  onTagDelete,
  role,
  onAllTagsAdd,
  onAllTagsDelete,
  onBulkTagAdd
}) => {
  const tagOptions: OptionProps[] = tags.cells.map((cell) => ({
    label: cell.value,
    value: cell.id,
    group: cell.column.name
  }))

  // List of policy.accessAttribute.tag.referencePublicId
  const tagPolicyReferenceIds = policies
    .filter(
      (policy) =>
        policy.accessAttribute?.kind == AccessAttributeKinds.TAG &&
        policy.accessAttribute?.tag != null &&
        isTagInCurrentTable(policy.accessAttribute.tag, tableId) &&
        policy.role === role
    )
    .map((p) => p.accessAttribute.tag?.referencePublicId)
    .filter((id): id is string => id != null)

  const selectedTagOptions = tagOptions.filter(
    (option) => option.value !== undefined && tagPolicyReferenceIds.includes(option.value)
  )

  // If there is a policy for the "Allow any tag" option
  const allTableTagsPolicy = policies.find((policy) => {
    if (
      policy.accessAttribute?.kind === AccessAttributeKinds.ALL_TABLE_TAGS &&
      policy.accessAttribute?.documentTable?.publicId === tableId &&
      policy.role === role
    ) {
      return true
    }
  })

  const handleChange = (option: string) => {
    if (!selectedTagOptions.find((o) => o.value == option)) {
      onTagAdd(option)
    } else {
      onTagDelete(option)
    }
  }

  const handleSelectAll = () => {
    if (allTableTagsPolicy) {
      onAllTagsDelete(tableId, role)
    } else {
      onAllTagsAdd(tableId, role)
    }
  }

  const active = selectedTagOptions.length > 0 || allTableTagsPolicy !== undefined

  return (
    <div className={`flex items-center transition-all rounded w-full m-2.5 ${active ? 'opacity-100' : 'opacity-50'}`}>
      <div className="font-bold truncate min-w-[200px] max-w-[200px]">{tags.name + ' Tags'}:</div>
      <div className="min-w-[200px] max-w-[200px]">
        <Checkbox checked={allTableTagsPolicy ? true : false} onChange={handleSelectAll}>
          At least one tag
        </Checkbox>
      </div>

      <div className="flex-1 ml-5 min-w-0">
        <Select
          options={tagOptions}
          onOptionClick={(option) => handleChange(option)}
          multiselect={true}
          groupBy={true}
          optionsSelected={selectedTagOptions.map((option) => (option.value ? option.value : ''))}
          disabled={allTableTagsPolicy ? true : false}
          setOptionsSelected={(options) => {
            const selected = options.filter((option) => option !== undefined)
            const newTags = tagOptions
              .filter((option) => (option.value ? selected.includes(option.value) : false))
              .map((option) => option.value)
            onBulkTagAdd(newTags as string[])
          }}
        />
      </div>
    </div>
  )
}

export default TagTable
